import { useCallback, useEffect, useRef, useState } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import type { Chapter, Setting, Volume, Faction, Character, Item, Conversation } from '../../types';
import { useEditorContext, EditorView, DataTab, SIDEBAR_VIEWS_WITH_CHILDREN } from './context';
import type { PaneId } from './paneLayout';
import { useWorkspace } from '../workspace/WorkspaceContext';
import { useAIChat, chatConvKey, GLOBAL_CHAT_BOOK_ID } from '../ai-chat';
import { dataCache } from '../../cache/DataCache';

type ResultKind = 'chapter' | 'outline' | 'setting' | 'volume' | 'faction' | 'character' | 'item' | 'conversation';

export interface SearchResult {
  id: string;
  kind: ResultKind;
  kindLabel: string;
  crumb: string;
  title: string;
  snippet: string;
  matchStart: number; // 相对 snippet 的高亮起点，-1 表示不高亮
  matchLen: number;
  score: number;
  onOpen: () => void;
}

/** 用于就地过滤菜单树的命中集合（不截断） */
export interface SearchMatch {
  chapterIds: Set<string>;
  outline: boolean;
  settingMatch: boolean;
  volumeIds: Set<string>;
  factionMatch: boolean;
  characterMatch: boolean;
  itemMatch: boolean;
  conversationIds: Set<string>;
}

interface SearchIndex {
  chapters: { ch: Chapter; plain: string }[];
  outline: string;
  settings: Setting[];
  volumes: Volume[];
  factions: Faction[];
  characters: Character[];
  items: Item[];
  conversations: Conversation[];
}

const KIND_LABEL: Record<ResultKind, string> = {
  chapter: '章节', outline: '大纲', setting: '设定', volume: '卷目', faction: '势力', character: '人物', item: '物品', conversation: '对话',
};

function emptyMatch(): SearchMatch {
  return { chapterIds: new Set(), outline: false, settingMatch: false, volumeIds: new Set(), factionMatch: false, characterMatch: false, itemMatch: false, conversationIds: new Set() };
}

export function matchTotal(m: SearchMatch | null): number {
  if (!m) return 0;
  return m.chapterIds.size + (m.outline ? 1 : 0) + (m.settingMatch ? 1 : 0) + m.volumeIds.size +
    (m.factionMatch ? 1 : 0) + (m.characterMatch ? 1 : 0) + (m.itemMatch ? 1 : 0) + m.conversationIds.size;
}

function stripHtml(html: string): string {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || '').replace(/\s+/g, ' ').trim();
}

/** 模糊匹配：优先子串命中，其次子序列命中。 */
function matchText(text: string, q: string): { score: number; index: number; contiguous: boolean } | null {
  if (!text) return null;
  const t = text.toLowerCase();
  const idx = t.indexOf(q);
  if (idx >= 0) return { score: 1000 - Math.min(idx, 500), index: idx, contiguous: true };
  let ti = 0, qi = 0, first = -1;
  while (ti < t.length && qi < q.length) {
    if (t[ti] === q[qi]) { if (first < 0) first = ti; qi++; }
    ti++;
  }
  if (qi === q.length) return { score: 200, index: first < 0 ? 0 : first, contiguous: false };
  return null;
}

function makeSnippet(text: string, index: number, qlen: number): { snippet: string; matchStart: number; matchLen: number } {
  const clean = text.replace(/\s+/g, ' ');
  const start = Math.max(0, index - 24);
  const end = Math.min(clean.length, index + qlen + 60);
  let snippet = clean.slice(start, end);
  let matchStart = index - start;
  if (start > 0) { snippet = '…' + snippet; matchStart += 1; }
  if (end < clean.length) snippet = snippet + '…';
  return { snippet, matchStart, matchLen: qlen };
}

function bestFieldMatch(fields: string[], q: string) {
  let best: { score: number; index: number; contiguous: boolean; value: string } | null = null;
  for (const value of fields) {
    const m = matchText(value, q);
    if (m && (!best || m.score > best.score)) best = { ...m, value };
  }
  return best;
}

export function useGlobalSearch() {
  const { setActiveView, setMenuExpanded, setDataTab, showPane } = useEditorContext();
  const { bookId, handleSelectChapter, numberToChinese, dataRefreshTrigger } = useWorkspace();
  const { handleSelectConv } = useAIChat();

  const [query, setQuery] = useState('');
  const [filterActive, setFilterActive] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [match, setMatch] = useState<SearchMatch | null>(null);
  const [loading, setLoading] = useState(false);
  const indexRef = useRef<SearchIndex | null>(null);

  useEffect(() => {
    indexRef.current = null;
  }, [bookId, dataRefreshTrigger]);

  // 章节内容/元数据保存后事件驱动失效索引（代替每次聚焦重建，避免大书卡顿）
  useEffect(() => {
    const invalidate = () => { indexRef.current = null; };
    window.addEventListener('xiaotianshu:data-changed', invalidate);
    return () => window.removeEventListener('xiaotianshu:data-changed', invalidate);
  }, []);

  const goto = useCallback((view: EditorView, opts?: { dataTab?: DataTab }) => {
    if (opts?.dataTab) setDataTab(opts.dataTab);
    setActiveView(view);
    if (SIDEBAR_VIEWS_WITH_CHILDREN.has(view)) setMenuExpanded(view, true);
  }, [setActiveView, setMenuExpanded, setDataTab]);

  /**
   * 跳到右侧面板：设定管理 / 资料库 / 伏笔追踪已迁到图标栏，
   * 命中它们时**把面板拉出来**（`showPane` 对已在分屏里的页不做任何事，
   * 因此不会把用户正开着的面板关掉），而不是切中心区域的页。
   */
  const showPaneFor = useCallback((id: PaneId, opts?: { dataTab?: DataTab }) => {
    if (opts?.dataTab) setDataTab(opts.dataTab);
    showPane(id);
  }, [showPane, setDataTab]);

  const ensureIndex = useCallback(async (): Promise<SearchIndex> => {
    if (indexRef.current) return indexRef.current;
    setLoading(true);
    try {
      const cachedVols = dataCache.getVolumes(bookId);
      const cachedSettings = dataCache.getSettings(bookId);
      const cachedFactions = dataCache.getFactions(bookId);
      const cachedCharacters = dataCache.getCharacters(bookId);
      const cachedItems = dataCache.getItems(bookId);
      const cachedOutline = dataCache.getOutline(bookId);

      if (cachedVols !== null && cachedSettings !== null && cachedFactions !== null &&
          cachedCharacters !== null && cachedItems !== null && cachedOutline !== null) {
        const chaptersByVol = dataCache.getChaptersByVolumeRaw(bookId) ?? {};
        const allChapters: Chapter[] = [];
        for (const chaps of Object.values(chaptersByVol)) {
          for (const ch of chaps) allChapters.push(ch);
        }
        const contentsMap = dataCache.getChapterContentsRaw(bookId);
        const chapters = allChapters.map(ch => ({
          ch,
          plain: stripHtml(contentsMap?.get(ch.id) ?? ''),
        }));
        let conversations: Conversation[] = [];
        try { conversations = JSON.parse(localStorage.getItem(chatConvKey(GLOBAL_CHAT_BOOK_ID)) ?? '[]') as Conversation[]; }
        catch { conversations = []; }
        const index: SearchIndex = {
          chapters, outline: stripHtml(cachedOutline ?? ''),
          settings: cachedSettings, volumes: cachedVols,
          factions: cachedFactions, characters: cachedCharacters,
          items: cachedItems, conversations,
        };
        indexRef.current = index;
        return index;
      }

      // Fallback: fetch from API
      const [chaptersMeta, outline, settings, volumes, factions, characters, items] = await Promise.all([
        window.api.getAllChaptersByBook(bookId),
        window.api.getOutline(bookId),
        window.api.getSettings(bookId),
        window.api.getVolumes(bookId),
        window.api.getFactions(bookId),
        window.api.getCharacters(bookId),
        window.api.getItems(bookId),
      ]);
      const contents = await Promise.all(
        chaptersMeta.map(c => window.api.getChapterContent(c.id).catch(() => ''))
      );
      const chapters = chaptersMeta.map((ch, i) => ({ ch, plain: stripHtml(contents[i] || '') }));
      let conversations: Conversation[] = [];
      try { conversations = JSON.parse(localStorage.getItem(chatConvKey(GLOBAL_CHAT_BOOK_ID)) ?? '[]') as Conversation[]; }
      catch { conversations = []; }
      const index: SearchIndex = { chapters, outline: stripHtml(outline || ''), settings, volumes, factions, characters, items, conversations };
      indexRef.current = index;
      return index;
    } finally {
      setLoading(false);
    }
  }, [bookId]);

  const compute = useCallback((index: SearchIndex, rawQ: string): { results: SearchResult[]; match: SearchMatch } => {
    const q = rawQ.trim().toLowerCase();
    const out: SearchResult[] = [];
    const mm = emptyMatch();
    if (!q) return { results: out, match: mm };

    const sortedVols = [...index.volumes].sort((a, b) => a.order_index - b.order_index);
    const volMeta = new Map<string, { title: string; idx: number }>();
    sortedVols.forEach((v, i) => volMeta.set(v.id, { title: v.title, idx: i + 1 }));

    const push = (
      base: Omit<SearchResult, 'snippet' | 'matchStart' | 'matchLen' | 'score'>,
      matchValue: string, m: { score: number; index: number; contiguous: boolean }, bonus = 0,
    ) => {
      const snip = makeSnippet(matchValue, m.index, q.length);
      out.push({ ...base, snippet: snip.snippet, matchStart: m.contiguous ? snip.matchStart : -1, matchLen: snip.matchLen, score: m.score + bonus });
    };

    for (const { ch, plain } of index.chapters) {
      const m = bestFieldMatch([ch.title, ch.summary || '', plain], q);
      if (m) {
        mm.chapterIds.add(ch.id);
        const vm = volMeta.get(ch.volume_id);
        const crumb = vm ? `章节列表 · 第${numberToChinese(vm.idx)}卷 ${vm.title}` : '章节列表';
        push({ id: `chapter-${ch.id}`, kind: 'chapter', kindLabel: KIND_LABEL.chapter, crumb, title: ch.title || '（未命名章节）', onOpen: () => handleSelectChapter(ch) }, m.value, m, 30);
      }
    }

    {
      const m = matchText(index.outline, q);
      if (m) { mm.outline = true; push({ id: 'outline', kind: 'outline', kindLabel: KIND_LABEL.outline, crumb: '作品设定 · 作品大纲', title: '作品大纲', onOpen: () => showPaneFor('settings') }, index.outline, m); }
    }

    for (const s of index.settings) {
      const m = bestFieldMatch([s.title, s.content || ''], q);
      if (m) { mm.settingMatch = true; push({ id: `setting-${s.id}`, kind: 'setting', kindLabel: KIND_LABEL.setting, crumb: '设定管理 · 世界观设置', title: s.title || '（未命名设定）', onOpen: () => showPaneFor('settings') }, m.value, m); }
    }

    for (const v of index.volumes) {
      const m = bestFieldMatch([v.title, v.description || ''], q);
      // 分卷的落点改为**章节列表**：卷目结构页已并入那里的节奏视图，
      // 而卷层级本来就在章节列表里直接可见（卷名 + 章数 + 字数）。
      if (m) { mm.volumeIds.add(v.id); push({ id: `volume-${v.id}`, kind: 'volume', kindLabel: KIND_LABEL.volume, crumb: '章节列表 · 分卷', title: v.title || '（未命名分卷）', onOpen: () => goto('chapters') }, m.value, m); }
    }

    for (const f of index.factions) {
      const m = bestFieldMatch([f.name, f.description || ''], q);
      if (m) { mm.factionMatch = true; push({ id: `faction-${f.id}`, kind: 'faction', kindLabel: KIND_LABEL.faction, crumb: '资料库 · 势力', title: f.name, onOpen: () => showPaneFor('data', { dataTab: 'factions' }) }, m.value, m); }
    }

    for (const c of index.characters) {
      const m = bestFieldMatch([c.name, c.description || '', c.strength || ''], q);
      if (m) { mm.characterMatch = true; push({ id: `character-${c.id}`, kind: 'character', kindLabel: KIND_LABEL.character, crumb: '资料库 · 人物', title: c.name, onOpen: () => showPaneFor('data', { dataTab: 'characters' }) }, m.value, m); }
    }

    for (const it of index.items) {
      const m = bestFieldMatch([it.name, it.type || '', it.description || ''], q);
      if (m) { mm.itemMatch = true; push({ id: `item-${it.id}`, kind: 'item', kindLabel: KIND_LABEL.item, crumb: '资料库 · 物品', title: it.name, onOpen: () => showPaneFor('data', { dataTab: 'items' }) }, m.value, m); }
    }

    for (const conv of index.conversations) {
      const fields = [conv.title, ...conv.messages.filter(msg => msg.role === 'user' || msg.role === 'assistant').map(msg => msg.content || '')];
      const m = bestFieldMatch(fields, q);
      if (m) { mm.conversationIds.add(conv.id); push({ id: `conversation-${conv.id}`, kind: 'conversation', kindLabel: KIND_LABEL.conversation, crumb: 'AI 对话', title: conv.title || '（未命名对话）', onOpen: () => { handleSelectConv(conv.id); setActiveView('ai-chat'); setMenuExpanded('ai-chat', true); } }, m.value, m); }
    }

    return { results: out.sort((a, b) => b.score - a.score).slice(0, 50), match: mm };
  }, [goto, showPaneFor, handleSelectChapter, handleSelectConv, numberToChinese, setActiveView, setMenuExpanded]);

  // 实时搜索（随输入变化，供悬浮下拉与就地过滤共用）
  useEffect(() => {
    const q = query.trim();
    if (!q) { setResults([]); setMatch(null); return; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      const index = await ensureIndex();
      if (cancelled) return;
      const r = compute(index, q);
      setResults(r.results);
      setMatch(r.match);
    }, 160);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query, ensureIndex, compute]);

  const handleChange = useCallback((v: string) => {
    setQuery(v);
    if (!v.trim()) {
      setFilterActive(false);
      setDropdownOpen(false);
    } else {
      // 过滤模式下继续就地过滤（不弹下拉）；否则显示悬浮下拉快速入口
      setDropdownOpen(!filterActive);
    }
  }, [filterActive]);

  const submit = useCallback(() => {
    if (query.trim()) { setFilterActive(true); setDropdownOpen(false); }
  }, [query]);

  const clear = useCallback(() => {
    setQuery('');
    setFilterActive(false);
    setDropdownOpen(false);
    setResults([]);
    setMatch(null);
  }, []);

  const pick = useCallback((r: SearchResult) => {
    r.onOpen();
    clear();
  }, [clear]);

  const onFocus = useCallback(() => {
    if (query.trim() && !filterActive) setDropdownOpen(true);
  }, [query, filterActive]);

  const closeDropdown = useCallback(() => setDropdownOpen(false), []);

  return { query, handleChange, submit, clear, pick, onFocus, closeDropdown, filterActive, dropdownOpen, results, match, loading };
}

function ResultItem({ r, onClick }: { r: SearchResult; onClick: () => void }) {
  return (
    <button className="global-search-result" onClick={onClick}>
      <span className="global-search-result-head">
        <span className={`global-search-badge badge-${r.kind}`}>{r.kindLabel}</span>
        <span className="global-search-result-crumb">{r.crumb}</span>
      </span>
      <span className="global-search-result-title">{r.title}</span>
      <span className="global-search-result-snippet">
        {r.matchStart >= 0 ? (
          <>
            {r.snippet.slice(0, r.matchStart)}
            <mark>{r.snippet.slice(r.matchStart, r.matchStart + r.matchLen)}</mark>
            {r.snippet.slice(r.matchStart + r.matchLen)}
          </>
        ) : r.snippet}
      </span>
    </button>
  );
}

export function GlobalSearchBox({
  query, onChange, onSubmit, onClear, onFocus, onPick, closeDropdown,
  dropdownOpen, results, loading,
}: {
  query: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  onClear: () => void;
  onFocus: () => void;
  onPick: (r: SearchResult) => void;
  closeDropdown: () => void;
  dropdownOpen: boolean;
  results: SearchResult[];
  loading: boolean;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const q = query.trim();
  const showDropdown = dropdownOpen && q.length > 0;

  // Ctrl+F（xiaotianshu:focus-search 事件）聚焦搜索框
  useEffect(() => {
    const focusSearch = () => {
      inputRef.current?.focus();
      inputRef.current?.select();
    };
    window.addEventListener('xiaotianshu:focus-search', focusSearch);
    return () => window.removeEventListener('xiaotianshu:focus-search', focusSearch);
  }, []);

  useEffect(() => {
    if (!showDropdown) return;
    const onDocDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) closeDropdown();
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [showDropdown, closeDropdown]);

  return (
    <div className="global-search" ref={rootRef}>
      <div className="global-search-input-wrap">
        <Search size={14} className="global-search-icon" />
        <input
          ref={inputRef}
          className="global-search-input"
          value={query}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') onSubmit(); }}
          onFocus={onFocus}
          placeholder="全局搜索：输入即搜，回车展开列表"
        />
        {query && (
          <button className="global-search-clear" onClick={onClear} title="清除">
            <X size={13} />
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="global-search-dropdown">
          {loading && results.length === 0 && (
            <div className="global-search-status"><Loader2 size={14} className="global-search-spin" /> 搜索中…</div>
          )}
          {!loading && results.length === 0 && (
            <div className="global-search-status">未找到与“{q}”匹配的内容</div>
          )}
          {results.length > 0 && <div className="global-search-count">快速入口 · {results.length} 条（回车展开列表）</div>}
          {results.map(r => (
            <ResultItem key={r.id} r={r} onClick={() => onPick(r)} />
          ))}
        </div>
      )}
    </div>
  );
}
